'use client';

import { useProgressBar } from '@react-aria/progress';
import { Children } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { filterProps } from '../../internal/utils/filterProps';
import { type Component, type ComponentProps } from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { mergeProps } from '../../utils/mergeProps';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';

type Variants = VariantProps<typeof spinnerStyles>;

const spinnerClasses = {
  root: 'zds-spinner-root',
  label: 'zds-spinner-label',
  svg: 'zds-spinner-svg',
  svgIndicator: 'zds-spinner-svg-indicator',
  svgTrack: 'zds-spinner-svg-track',
} as const;

const spinnerStyles = tv({
  slots: {
    root: [spinnerClasses.root, 'inline-block', 'relative'],
    label: [
      spinnerClasses.label,
      'absolute',
      'inset-0',
      'flex',
      'items-center',
      'justify-center',
    ],
    svg: [
      spinnerClasses.svg,
      'block',
      'rounded-full',
      'overflow-hidden',
      'animate-[spin_1s_linear_infinite]',
    ],
    svgIndicator: [spinnerClasses.svgIndicator, 'stroke-current'],
    svgTrack: [
      spinnerClasses.svgTrack,
      'z--spinner-track-color',
      'z--spinner-track-opacity',
    ],
  },
  variants: {
    color: {
      default: {
        svgIndicator: 'z--spinner-default-indicator-color',
      },
      'static-white': {
        svgIndicator: 'stroke-static-white-main',
        svgTrack: 'stroke-static-white-main',
      },
      inherit: {
        svg: 'text-inherit',
      },
      primary: {
        svg: 'text-accent-primary-main',
      },
      secondary: {
        svg: 'text-accent-secondary-main',
      },
      success: {
        svg: 'text-success-main',
      },
      error: {
        svg: 'text-error-main',
      },
      white: {
        svgIndicator: 'stroke-static-white-main',
        svgTrack: 'stroke-static-white-main',
      },
    },
    size: {
      xs: {
        root: 'z--spinner-xs-height z--spinner-xs-width',
        svgIndicator: 'z--spinner-xs-border-width',
        svgTrack: 'z--spinner-xs-border-width',
      },
      sm: {
        root: 'z--spinner-sm-height z--spinner-sm-width',
        svgIndicator: 'z--spinner-sm-border-width',
        svgTrack: 'z--spinner-sm-border-width',
      },
      md: {
        root: 'z--spinner-md-height z--spinner-md-width',
        svgIndicator: 'z--spinner-md-border-width',
        svgTrack: 'z--spinner-md-border-width',
      },
      lg: {
        root: 'z--spinner-lg-height z--spinner-lg-width',
        svgIndicator: 'z--spinner-lg-border-width',
        svgTrack: 'z--spinner-lg-border-width',
      },
      xl: {
        root: 'z--spinner-xl-height z--spinner-xl-width',
        svgIndicator: 'z--spinner-xl-border-width',
        svgTrack: 'z--spinner-xl-border-width',
      },
    },
    sizeXs: {
      xs: {
        root: 'xs:z--spinner-xs-height xs:z--spinner-xs-width',
        svgIndicator: 'xs:z--spinner-xs-border-width',
        svgTrack: 'xs:z--spinner-xs-border-width',
      },
      sm: {
        root: 'xs:z--spinner-sm-height xs:z--spinner-sm-width',
        svgIndicator: 'xs:z--spinner-sm-border-width',
        svgTrack: 'xs:z--spinner-sm-border-width',
      },
      md: {
        root: 'xs:z--spinner-md-height xs:z--spinner-md-width',
        svgIndicator: 'xs:z--spinner-md-border-width',
        svgTrack: 'xs:z--spinner-md-border-width',
      },
      lg: {
        root: 'xs:z--spinner-lg-height xs:z--spinner-lg-width',
        svgIndicator: 'xs:z--spinner-lg-border-width',
        svgTrack: 'xs:z--spinner-lg-border-width',
      },
      xl: {
        root: 'xs:z--spinner-xl-height xs:z--spinner-xl-width',
        svgIndicator: 'xs:z--spinner-xl-border-width',
        svgTrack: 'xs:z--spinner-xl-border-width',
      },
    },
    sizeSm: {
      xs: {
        root: 'sm:z--spinner-xs-height sm:z--spinner-xs-width',
        svgIndicator: 'sm:z--spinner-xs-border-width',
        svgTrack: 'sm:z--spinner-xs-border-width',
      },
      sm: {
        root: 'sm:z--spinner-sm-height sm:z--spinner-sm-width',
        svgIndicator: 'sm:z--spinner-sm-border-width',
        svgTrack: 'sm:z--spinner-sm-border-width',
      },
      md: {
        root: 'sm:z--spinner-md-height sm:z--spinner-md-width',
        svgIndicator: 'sm:z--spinner-md-border-width',
        svgTrack: 'sm:z--spinner-md-border-width',
      },
      lg: {
        root: 'sm:z--spinner-lg-height sm:z--spinner-lg-width',
        svgIndicator: 'sm:z--spinner-lg-border-width',
        svgTrack: 'sm:z--spinner-lg-border-width',
      },
      xl: {
        root: 'sm:z--spinner-xl-height sm:z--spinner-xl-width',
        svgIndicator: 'sm:z--spinner-xl-border-width',
        svgTrack: 'sm:z--spinner-xl-border-width',
      },
    },
    sizeMd: {
      xs: {
        root: 'md:z--spinner-xs-height md:z--spinner-xs-width',
        svgIndicator: 'md:z--spinner-xs-border-width',
        svgTrack: 'md:z--spinner-xs-border-width',
      },
      sm: {
        root: 'md:z--spinner-sm-height md:z--spinner-sm-width',
        svgIndicator: 'md:z--spinner-sm-border-width',
        svgTrack: 'md:z--spinner-sm-border-width',
      },
      md: {
        root: 'md:z--spinner-md-height md:z--spinner-md-width',
        svgIndicator: 'md:z--spinner-md-border-width',
        svgTrack: 'md:z--spinner-md-border-width',
      },
      lg: {
        root: 'md:z--spinner-lg-height md:z--spinner-lg-width',
        svgIndicator: 'md:z--spinner-lg-border-width',
        svgTrack: 'md:z--spinner-lg-border-width',
      },
      xl: {
        root: 'md:z--spinner-xl-height md:z--spinner-xl-width',
        svgIndicator: 'md:z--spinner-xl-border-width',
        svgTrack: 'md:z--spinner-xl-border-width',
      },
    },
    sizeLg: {
      xs: {
        root: 'lg:z--spinner-xs-height lg:z--spinner-xs-width',
        svgIndicator: 'lg:z--spinner-xs-border-width',
        svgTrack: 'lg:z--spinner-xs-border-width',
      },
      sm: {
        root: 'lg:z--spinner-sm-height lg:z--spinner-sm-width',
        svgIndicator: 'lg:z--spinner-sm-border-width',
        svgTrack: 'lg:z--spinner-sm-border-width',
      },
      md: {
        root: 'lg:z--spinner-md-height lg:z--spinner-md-width',
        svgIndicator: 'lg:z--spinner-md-border-width',
        svgTrack: 'lg:z--spinner-md-border-width',
      },
      lg: {
        root: 'lg:z--spinner-lg-height lg:z--spinner-lg-width',
        svgIndicator: 'lg:z--spinner-lg-border-width',
        svgTrack: 'lg:z--spinner-lg-border-width',
      },
      xl: {
        root: 'lg:z--spinner-xl-height lg:z--spinner-xl-width',
        svgIndicator: 'lg:z--spinner-xl-border-width',
        svgTrack: 'lg:z--spinner-xl-border-width',
      },
    },
    sizeXl: {
      xs: {
        root: 'xl:z--spinner-xs-height xl:z--spinner-xs-width',
        svgIndicator: 'xl:z--spinner-xs-border-width',
        svgTrack: 'xl:z--spinner-xs-border-width',
      },
      sm: {
        root: 'xl:z--spinner-sm-height xl:z--spinner-sm-width',
        svgIndicator: 'xl:z--spinner-sm-border-width',
        svgTrack: 'xl:z--spinner-sm-border-width',
      },
      md: {
        root: 'xl:z--spinner-md-height xl:z--spinner-md-width',
        svgIndicator: 'xl:z--spinner-md-border-width',
        svgTrack: 'xl:z--spinner-md-border-width',
      },
      lg: {
        root: 'xl:z--spinner-lg-height xl:z--spinner-lg-width',
        svgIndicator: 'xl:z--spinner-lg-border-width',
        svgTrack: 'xl:z--spinner-lg-border-width',
      },
      xl: {
        root: 'xl:z--spinner-xl-height xl:z--spinner-xl-width',
        svgIndicator: 'xl:z--spinner-xl-border-width',
        svgTrack: 'xl:z--spinner-xl-border-width',
      },
    },
  },
});

export type SpinnerColorValue = 'default' | 'static-white' | 'inherit';

/**
 * @deprecated These colour values are deprecated.
 * Replace `'primary'` with `'default'`,
 * `'white'` with `'static-white'`.
 * The values `'secondary'`, `'success'`, and `'error'` have no replacement — remove the prop.
 */
export type SpinnerDeprecatedColorValue =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'white';

interface SpinnerBaseProps {
  /**
   * The colour of the spinner.
   *
   * - `'default'`.
   * - `'static-white'` — always white, regardless of theme.
   * - `'inherit'` — inherits the colour from the parent element.
   *
   * The values `'primary'`, `'secondary'`, `'success'`, `'error'`, and `'white'` are deprecated.
   * Use `'default'` instead of `'primary'`, and `'static-white'` instead of `'white'`.
   *
   * @default 'default'
   */
  color?: SpinnerColorValue | SpinnerDeprecatedColorValue;
  /**
   * The size of the spinner. Accepts a scalar value or a responsive object.
   * @default 'lg'
   * @example 'md'
   * @example { default: 'sm', lg: 'md' }
   */
  size?: ResponsiveOption<NonNullable<Variants['size']>>;
}

type SpinnerProps = ComponentProps<SpinnerBaseProps, 'span'>;

type SpinnerComponent = Component<SpinnerBaseProps, 'span'>;

const Spinner: SpinnerComponent = (props) => {
  const [localProps, progressBarRaProps] = filterProps(props, [
    'useProgressBar',
  ]);

  const {
    color = 'default',
    size: sizeProp,
    className,
    children,
    ref,
    ...restLocalProps
  } = localProps;

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const resolvedSize = defaultSize ?? 'lg';

  const { progressBarProps, labelProps } = useProgressBar({
    ...progressBarRaProps,
    isIndeterminate: true,
    label: children,
  });

  const hasChildren = Children.count(children) > 0;

  const styles = spinnerStyles({
    color,
    size: resolvedSize,
    sizeXs: responsiveSize?.xs,
    sizeSm: responsiveSize?.sm,
    sizeMd: responsiveSize?.md,
    sizeLg: responsiveSize?.lg,
    sizeXl: responsiveSize?.xl,
  });

  // SVG coordinate values are defined in CSS custom properties:
  // --zds-spinner-viewbox-size: 100
  // --zds-spinner-center: 50
  // --zds-spinner-radius: 45
  const viewBoxSize = 100;
  const center = 50;
  const circleRadius = 45;

  const viewBox = `0 0 ${viewBoxSize} ${viewBoxSize}`;

  return (
    <span
      {...mergeProps(restLocalProps, progressBarProps)}
      className={styles.root({ className })}
      ref={ref}
    >
      <svg className={styles.svg()} viewBox={viewBox}>
        <circle
          className={styles.svgTrack()}
          cx={center}
          cy={center}
          r={circleRadius}
          fill="none"
          vectorEffect="non-scaling-stroke"
          role="presentation"
        />
        <circle
          className={styles.svgIndicator()}
          cx={center}
          cy={center}
          r={circleRadius}
          fill="none"
          vectorEffect="non-scaling-stroke"
          role="presentation"
        />
      </svg>
      {hasChildren && (
        <span {...labelProps} className={styles.label()}>
          {children}
        </span>
      )}
    </span>
  );
};

Spinner.displayName = 'Spinner';

export { Spinner, type SpinnerProps, type SpinnerBaseProps, spinnerClasses };
